import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Feather, Clock } from "lucide-react";

export default function Login() {
  const { login, setupComplete, loading, author } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const reason = params.get("reason");

  useEffect(() => {
    if (loading) return;
    if (setupComplete === false) nav("/setup");
    if (author) nav("/dashboard");
  }, [loading, setupComplete, author, nav]);

  useEffect(() => {
    if (reason === "idle") {
      toast.info("Signed out after 30 minutes of inactivity. Please sign in again.",
                 { duration: 6000 });
    } else if (reason === "expired") {
      toast.info("Session expired. Sign back in — any open draft has been saved locally.",
                 { duration: 6000 });
    }
  }, [reason]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign-in failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2" data-testid="login-page">
      <div className="hidden md:block relative overflow-hidden"
           style={{ background: "var(--elle-surface)" }}>
        <img
          src="https://images.unsplash.com/photo-1773916398209-84b1a5d95763?crop=entropy&cs=srgb&fm=jpg&w=1400&q=70"
          alt="A vintage brass desk lamp illuminating a library"
          className="absolute inset-0 w-full h-full object-cover opacity-55"
        />
        <div className="absolute inset-0"
             style={{ background: "linear-gradient(120deg, rgba(11,14,20,0.7), rgba(11,14,20,0.35))" }}/>
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-2">
            <span className="lamp-dot" />
            <span className="overline">Sanctuary</span>
          </div>
          <div>
            <h1 className="font-display text-5xl leading-tight max-w-md"
                style={{ color: "var(--elle-ink)" }}>
              The Boggin Chronicles
            </h1>
            <p className="mt-4 max-w-md font-display text-xl italic"
               style={{ color: "var(--elle-ink-dim)" }}>
              "Less about me. More about the Orc."
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6 fade-up">
          <div>
            <div className="flex items-center gap-2 overline">
              <Feather size={14}/> Elle's sanctuary
            </div>
            <h2 className="font-display text-4xl mt-2">Welcome back.</h2>
            <p className="text-sm mt-2" style={{ color: "var(--elle-ink-dim)" }}>
              A quiet place to manage a public life.
            </p>
            {reason === "idle" && (
              <div className="mt-4 p-3 rounded-sm flex items-start gap-2 text-xs"
                   data-testid="idle-notice"
                   style={{ border: "1px solid var(--elle-amber)",
                            background: "rgba(197,160,89,0.08)",
                            color: "var(--elle-amber)" }}>
                <Clock size={14} className="mt-0.5 shrink-0"/>
                <span>Signed out after 30 minutes of inactivity — the sensible thing for a shared desk.</span>
              </div>
            )}
            {reason === "expired" && (
              <div className="mt-4 p-3 rounded-sm flex items-start gap-2 text-xs"
                   data-testid="expired-notice"
                   style={{ border: "1px solid var(--elle-amber)",
                            background: "rgba(197,160,89,0.08)",
                            color: "var(--elle-amber)" }}>
                <Clock size={14} className="mt-0.5 shrink-0"/>
                <span>Session expired. Sign back in — any draft you had open has been kept safe in this browser.</span>
              </div>
            )}
          </div>
          <div className="space-y-4">
            <div>
              <label className="overline block mb-2">Email</label>
              <Input data-testid="login-email" type="email" required
                     value={email} onChange={(e)=>setEmail(e.target.value)}/>
            </div>
            <div>
              <label className="overline block mb-2">Password</label>
              <Input data-testid="login-password" type="password" required
                     value={password} onChange={(e)=>setPassword(e.target.value)}/>
            </div>
          </div>
          <Button data-testid="login-submit" type="submit" disabled={busy}
                  className="btn-amber rounded-sm w-full h-11">
            {busy ? "Signing in…" : "Enter the study"}
          </Button>
          <div className="flex items-center justify-between text-xs font-mono-elle">
            <span style={{ color: "var(--elle-ink-faint)" }}>Filtered. Summarised. Private.</span>
            <a href="/recovery" data-testid="recovery-link" className="underline hover:text-[var(--elle-amber)]"
               style={{ color: "var(--elle-ink-dim)" }}>
              Lost access?
            </a>
          </div>
        </form>
      </div>
    </div>
  );
}
